import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';

/**
 * Reconciliation sections shown (collapsed) under the Accounting report table:
 * Payments received · AR roll-forward · Wallet liability · Gift cards.
 * Shapes match the backend (reportService.getTransactionStatistics).
 */

export interface ArRollForward {
    opening: number;
    billed: number;
    collected: number;
    cancelled: number;
    writtenOff: number;
    expectedClosing: number;
    closing: number;
    difference: number;
}

export interface WalletRollForward {
    opening: number;
    credits: { refunds: number; adminCredits: number; giftCardRedemptions: number; total: number };
    applied: number;
    closing: number;
}

export interface GiftCardSummary {
    issued: { count: number; amount: number };
    redeemed: { count: number; amount: number };
    outstanding: { count: number; amount: number };
    expiredUnredeemed: { count: number; amount: number };
}

export interface PaymentsReceived {
    lines: Array<{ key: string; label: string; postTo: string; amount: number; count: number }>;
    total: number;
}

export interface ReconciliationData {
    arRollForward?: ArRollForward;
    walletRollForward?: WalletRollForward;
    giftCards?: GiftCardSummary;
    paymentsReceived?: PaymentsReceived;
}

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

type Line = { label: string; amount: number; note?: string; strong?: boolean; indent?: boolean };

/** Label / amount rows for each section — shared by the UI and the CSV export */
export function reconciliationSections(data: ReconciliationData): Array<{ title: string; description: string; lines: Line[] }> {
    const sections: Array<{ title: string; description: string; lines: Line[] }> = [];

    if (data.paymentsReceived) {
        sections.push({
            title: 'Payments received',
            description: 'How the TOTAL Net came in, and where to post it. Member-account Food & Beverage / Pro Shop sales are shown as charged to the card; their refunds are in the Refunds lines by destination (the tip part of a refund is excluded — it comes off the tips payable).',
            lines: [
                ...data.paymentsReceived.lines.map(l => ({
                    label: `${l.label} (${l.count})`,
                    amount: l.amount,
                    note: `→ ${l.postTo}`
                })),
                { label: 'Total (equals TOTAL Net)', amount: data.paymentsReceived.total, strong: true }
            ]
        });
    }

    const ar = data.arRollForward;
    if (ar) {
        sections.push({
            title: 'Accounts receivable roll-forward',
            description: 'Opening AR + billed − collected − cancelled − written off = closing AR. Shop and F&B sales are collected at checkout and not included (unresolved member-account charges show as Outstanding on their own rows).',
            lines: [
                { label: 'Opening AR', amount: ar.opening, strong: true },
                { label: 'Billed in period', amount: ar.billed, indent: true },
                { label: 'Collected', amount: -ar.collected, indent: true },
                { label: 'Cancelled', amount: -ar.cancelled, indent: true },
                { label: 'Written off', amount: -ar.writtenOff, indent: true },
                { label: 'Closing AR', amount: ar.closing, strong: true },
                ...(Math.abs(ar.difference) > 0.004
                    ? [{ label: 'Difference (closing − opening − movements) — please review', amount: ar.difference }]
                    : [])
            ]
        });
    }

    const w = data.walletRollForward;
    if (w) {
        sections.push({
            title: 'Wallet liability roll-forward',
            description: 'Member wallet balances, gross. The HST on a refund to the wallet is reversed on the refund line, so the wallet carries the full amount.',
            lines: [
                { label: 'Opening balance', amount: w.opening, strong: true },
                { label: 'Refunds to wallet', amount: w.credits.refunds, indent: true },
                { label: 'Admin credits', amount: w.credits.adminCredits, indent: true },
                { label: 'Gift cards redeemed', amount: w.credits.giftCardRedemptions, indent: true },
                { label: 'Used to pay charges', amount: -w.applied, indent: true },
                { label: 'Closing balance', amount: w.closing, strong: true }
            ]
        });
    }

    const g = data.giftCards;
    if (g) {
        sections.push({
            title: 'Gift cards',
            description: 'Gift card sales are taken outside RallyOn (post Dr Bank / Cr Gift card liability). A redeemed card is loaded onto the member\'s wallet. No HST on gift cards.',
            lines: [
                { label: `Issued in period (${g.issued.count})`, amount: g.issued.amount },
                { label: `Redeemed in period (${g.redeemed.count})`, amount: g.redeemed.amount },
                { label: `Outstanding at period end (${g.outstanding.count})`, amount: g.outstanding.amount, strong: true },
                { label: `Expired unredeemed at period end (${g.expiredUnredeemed.count})`, amount: g.expiredUnredeemed.amount }
            ]
        });
    }

    return sections;
}

export const AccountingReconciliation: React.FC<{ data: ReconciliationData }> = ({ data }) => {
    const sections = reconciliationSections(data);
    if (sections.length === 0) return null;

    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Reconciliation</Typography>
            {sections.map(section => (
                <Accordion key={section.title} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography sx={{ fontWeight: 600 }}>{section.title}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {section.description}
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell align="right">Amount</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {section.lines.map((line, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ pl: line.indent ? 4 : 2, fontWeight: line.strong ? 600 : 'normal' }}>
                                            {line.label}
                                        </TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{line.note ?? ''}</TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{ fontWeight: line.strong ? 600 : 'normal', color: line.amount < 0 ? 'error.main' : 'inherit' }}
                                        >
                                            {formatCurrency(line.amount)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};
